import type { ReactNode } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Screen container that keeps content clear of the system bars.
 *
 * This is not cosmetic. The app targets SDK 36, and from Android 15 the system
 * FORCES edge-to-edge for apps targeting 35+ — `edgeToEdgeEnabled=false` in
 * gradle.properties no longer opts out. So the window really does extend behind
 * the status bar and the navigation bar, and anything drawn at either edge sits
 * under system chrome.
 *
 * BOTH insets are applied, always.
 *
 * The bottom one used to be opt-in via a `bottom` prop, on the reasoning that inside a
 * bottom-tab screen the tab bar already consumed it and padding twice would leave a
 * visible dead band. That reasoning died with the tab bar: navigation is a plain native
 * stack now (see AuthenticatedShell — a stack deliberately, so a half-built journey
 * cannot be abandoned by tapping a tab). Nothing consumes the bottom inset any more, so
 * nine of the fifteen screens were drawing their primary CTA underneath the Android
 * navigation bar. On HyperOS that bar also comes and goes with the gesture-nav setting,
 * so the same screen looked correct and then did not.
 *
 * The floor matters for the disappearing case: with the bar hidden the inset is 0, and a
 * 58pt CTA flush against the display edge is its own problem. Screens carry their own
 * footer padding on top of this, so the floor only has to cover that one case.
 */
export function Screen({ children, style }: { children: ReactNode; style?: object }) {
  const insets = useSafeAreaInsets();
  const { c, space } = useTheme();
  return (
    <View
      style={[
        { flex: 1, backgroundColor: c.bg },
        { paddingTop: insets.top, paddingBottom: Math.max(insets.bottom, space.sm) },
        style,
      ]}
    >
      {children}
    </View>
  );
}
